import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'

import { validateEmail } from '../../../../../utilities/api'
import {
  emailPatternMatch,
  isBlockedEmail,
  namePatternMatch,
} from '../../../../../utilities/helpers'
import { Spinner } from '../../../../LoadingContainer/Spinner'
import { MarketingOptIn } from '../MarketingOptIn'

export default function GuestContact({
  onConfirm,
  disabled = false,
  submitError = '',
  organizationName,
  optInMessage,
}) {
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [emailError, setEmailError] = useState('')
  const [isChecking, setIsChecking] = useState(false)
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(false)

  const canSubmit =
    Boolean(email.trim() && firstName.trim() && lastName.trim()) &&
    emailPatternMatch(email) &&
    !isChecking &&
    !disabled

  const handleEmailChange = (value) => {
    setEmail(value)
    setEmailError('')
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return

    if (!emailPatternMatch(email) || isBlockedEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    setIsChecking(true)
    try {
      // Same SendGrid verdict handling as the login form: a mistyped domain
      // means the buyer would never receive their tickets.
      const res = await validateEmail({ email })
      if (
        (res.data?.verdict === 'Risky' && res.data?.suggestion) ||
        res.data?.verdict === 'Invalid'
      ) {
        setEmailError('Please enter a valid email address')
        setIsChecking(false)
        return
      }
    } catch (err) {
      // Don't block checkout when the validation service is unavailable.
      console.error('Email validation failed:', err)
    }

    setIsChecking(false)
    onConfirm({
      email: email.trim().toLowerCase(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      marketingEmailOptIn,
    })
  }

  return (
    <Card className="card-xl card-xl--light mb-4">
      <Card.Body>
        <Card.Title as="h5" className="card-title-sm">
          Where should we send your tickets?
        </Card.Title>
        <p className="text-muted small">
          No account needed. We'll email your tickets right after payment.
        </p>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="form-group" controlId="guestEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              placeholder="Enter your email"
              required
              value={email}
              disabled={disabled}
              className={emailError ? 'error-border' : ''}
              onChange={(e) => handleEmailChange(e.target.value)}
            />
          </Form.Group>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="form-group" controlId="guestFirstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  placeholder="Enter your first name"
                  required
                  pattern={namePatternMatch}
                  value={firstName}
                  disabled={disabled}
                  onChange={(e) =>
                    setFirstName((name) =>
                      e.target.validity.valid || e.target.value === ''
                        ? e.target.value
                        : name
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="form-group" controlId="guestLastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  placeholder="Enter your last name"
                  required
                  pattern={namePatternMatch}
                  value={lastName}
                  disabled={disabled}
                  onChange={(e) =>
                    setLastName((name) =>
                      e.target.validity.valid || e.target.value === ''
                        ? e.target.value
                        : name
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          {(emailError || submitError) && (
            <Alert variant="danger" className="gap-3 mt-3">
              <div className="body">
                <p>{emailError || submitError}</p>
              </div>
            </Alert>
          )}
          <MarketingOptIn
            checked={marketingEmailOptIn}
            onChange={setMarketingEmailOptIn}
            organizationName={organizationName}
            optInMessage={optInMessage}
            disabled={disabled}
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={!canSubmit}
            className="icon-button w-100 mt-3"
          >
            {isChecking ? (
              <>
                <Spinner />
                <span>Checking...</span>
              </>
            ) : (
              <span>Continue to payment</span>
            )}
          </Button>
        </Form>
        <p className="caption text-muted text-center mt-3 mb-0">
          Already have an account?{' '}
          <Link to="/login" state={{ from: location }}>
            Sign in
          </Link>
        </p>
      </Card.Body>
    </Card>
  )
}
